import React, { useState, useEffect, useRef } from 'react';
import { FiSend, FiChevronDown, FiPaperclip, FiX, FiFile } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmailService from '../../../services/EmailService';
import RecipientInput from './RecipientInput';

const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024; // 20MB per file
const MAX_TOTAL_ATTACHMENTS_SIZE = 25 * 1024 * 1024; // 25MB total

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'blockquote'],
    ['clean'],
  ],
};

const quillFormats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'link',
  'blockquote',
];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ComposePanel = ({ accounts, defaultFromAccountId, preFill, onSent, onCancel }) => {
  const [fromAccountId, setFromAccountId] = useState(defaultFromAccountId || accounts?.[0]?.id);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [form, setForm] = useState({ subject: '', body: '' });
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setForm({
      subject: preFill?.subject || '',
      body: preFill?.body || '',
    });
    setAttachments([]);
    setError('');
    // Pre-select the account that received the original email (reply/forward)
    if (preFill?.fromAccountId) setFromAccountId(preFill.fromAccountId);
  }, [preFill]);

  useEffect(() => {
    if (defaultFromAccountId) setFromAccountId(defaultFromAccountId);
  }, [defaultFromAccountId]);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleBodyChange = (value) =>
    setForm((prev) => ({ ...prev, body: value }));

  const fromAccount = accounts?.find((a) => a.id === fromAccountId) || accounts?.[0];
  const multipleAccounts = (accounts?.length || 0) > 1;

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    const oversized = files.find((f) => f.size > MAX_ATTACHMENT_SIZE);
    if (oversized) {
      setError(`"${oversized.name}" exceeds the 20MB attachment limit.`);
      return;
    }

    const currentTotal = attachments.reduce((sum, a) => sum + a.size, 0);
    const newTotal = currentTotal + files.reduce((sum, f) => sum + f.size, 0);
    if (newTotal > MAX_TOTAL_ATTACHMENTS_SIZE) {
      setError('Total attachments size exceeds the 25MB limit.');
      return;
    }

    try {
      const encoded = await Promise.all(
        files.map(async (file) => ({
          id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          base64: await readFileAsBase64(file),
        }))
      );
      setAttachments((prev) => [...prev, ...encoded]);
      setError('');
    } catch {
      setError('Failed to read one or more files. Please try again.');
    }
  };

  const handleRemoveAttachment = (id) =>
    setAttachments((prev) => prev.filter((a) => a.id !== id));

  const handleSend = async () => {
    if (recipientEmails.length === 0 || !form.subject || !form.body) {
      setError('Please fill in all fields.');
      return;
    }
    if (!fromAccount) { setError('No account selected.'); return; }

    setSending(true);
    setError('');
    try {
      const attachmentPayload = attachments.map((a) => ({
        fileName: a.name,
        content: a.base64,
        contentType: a.type,
      }));

      if (fromAccount.type === 'gmail') {
        await EmailService.sendGmail(recipientEmails.join(','), form.subject, form.body, attachmentPayload);
      } else if (fromAccount.type === 'zoho') {
        await EmailService.sendZoho(recipientEmails.join(','), form.subject, form.body, [], [], true, attachmentPayload);
      } else {
        // TODO: wire up SMTP send via backend API using fromAccount.config
        throw new Error('SMTP sending not yet implemented');
      }
      setForm({ subject: '', body: '' });
      setAttachments([]);
      if (onSent) onSent();
    } catch (err) {
      const backendError = err?.response?.data?.error;
      setError(backendError || 'Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const initialEmails = preFill?.to
    ? preFill.to.split(',').map((e) => e.trim()).filter(Boolean)
    : [];

  return (
    <div className="ec-compose-panel">
      <div className="ec-compose-header">
        <span className="ec-compose-title">{preFill?.title || 'New Message'}</span>
      </div>

      <div className="ec-compose-fields">
        {/* From — always visible; dropdown only when multiple accounts */}
        <div className="ec-compose-field">
          <span className="ec-compose-label">From</span>
          {multipleAccounts ? (
            <div className="ec-compose-from-wrap">
              <select
                className="ec-compose-from-select"
                value={fromAccountId}
                onChange={(e) => setFromAccountId(e.target.value)}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.email} ({a.label})
                  </option>
                ))}
              </select>
              <FiChevronDown size={13} className="ec-compose-from-chevron" />
            </div>
          ) : (
            <span className="ec-compose-from-readonly">
              {fromAccount?.email || ''}
              <span className="ec-provider-badge" style={{ marginLeft: 6 }}>{fromAccount?.label}</span>
            </span>
          )}
        </div>

        <div className="ec-compose-field">
          <span className="ec-compose-label">To</span>
          <RecipientInput
            key={preFill?.to ?? 'compose'}
            initialEmails={initialEmails}
            onChange={setRecipientEmails}
          />
        </div>

        <div className="ec-compose-field">
          <span className="ec-compose-label">Subject</span>
          <input
            className="ec-compose-input"
            placeholder="Email subject"
            value={form.subject}
            onChange={handleChange('subject')}
          />
        </div>
      </div>

      <div className="ec-compose-body-wrap">
        <ReactQuill
          className="ec-compose-quill"
          theme="snow"
          value={form.body}
          onChange={handleBodyChange}
          modules={quillModules}
          formats={quillFormats}
          placeholder="Write your message..."
        />

        <div className="ec-compose-attachments-bar">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFilesSelected}
          />
          <button
            type="button"
            className="ec-compose-attach-btn"
            onClick={handleAttachClick}
            disabled={sending}
          >
            <FiPaperclip size={14} /> Attach files
          </button>

          {attachments.length > 0 && (
            <div className="ec-attachment-chip-list">
              {attachments.map((a) => (
                <span className="ec-attachment-chip" key={a.id}>
                  <FiFile size={12} />
                  <span className="ec-attachment-chip-name">{a.name}</span>
                  <span className="ec-attachment-chip-size">{formatFileSize(a.size)}</span>
                  <button
                    type="button"
                    className="ec-attachment-chip-remove"
                    onClick={() => handleRemoveAttachment(a.id)}
                    disabled={sending}
                    aria-label={`Remove ${a.name}`}
                  >
                    <FiX size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="ec-compose-footer">
        {error && (
          <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {onCancel && (
            <button className="ec-compose-cancel-btn" onClick={onCancel} disabled={sending}>
              Cancel
            </button>
          )}
          <button className="ec-compose-send-btn" onClick={handleSend} disabled={sending}>
            {sending
              ? <><div className="ec-spinner" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)' }} /> Sending...</>
              : <><FiSend size={14} /> Send</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComposePanel;
